import React from "react";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import { translateService, signService } from "../../services/imports";
import signers from "../../services/signerManagementService";
import { LoadingButton } from "../../components/formcontrols/imports";
import { CloudSignerNotice } from "../../components/signers/cloudNotice";
import CertificateFields, {
  UnitField,
  emptyCertificate,
  checkCertificate,
  certificateBody,
  subjectFieldOf,
  unitFrom,
  vettedSubjectRefused,
} from "./certificateFields";
import EvidenceFields, { keepsEvidence, defaultEvidence, evidenceBody, evidenceValid } from "./evidenceFields";

const t = (k, ...a) => translateService.t(`signers.add.${k}`, ...a);

// The "Add a signing tool" dialog, driven by the gateway's catalogue (fetched
// whenever the dialog opens: no-store). Steps: the kind (the list's
// creatableKinds, and its unavailableKinds disabled with their reason - what
// an organization's account offers - never hidden); for a cloud kind its
// certification, from the workspace's certification options (read whenever
// that step opens: unavailable ones disabled with their reason), then the
// certificate's subject (a vetted organization's identity read-only, an
// identifier picked; otherwise typed); then, for a kind the gateway does not
// make by itself, the name, for a cloud kind the name in the certificate
// below it, the authentication types and, where a type uses one, the
// verification engine. An auto-provisioned kind (Georgian eID) is "the
// workspace's own": added at once, or found (200).
//
// props: isOpen, workspaceId, creatableKinds, unavailableKinds, onClose(),
// onCreated(signer)
export default class AddSignerDialog extends React.Component {
  state = this.initialState();

  initialState() {
    return {
      catalogue: null,
      catalogueError: null,
      options: null, // signer-certification-options
      optionsError: null,
      step: "kind",
      kind: null,
      cert: emptyCertificate(null),
      name: "",
      authTypes: [],
      engine: "",
      // a face-verified tool's verification evidence (the PDF stays off)
      evidence: defaultEvidence(),
      errors: {},
      sending: false,
      refusal: null,
    };
  }

  componentDidMount() {
    if (this.props.isOpen) this.open();
  }

  componentDidUpdate(prev) {
    if (this.props.isOpen && !prev.isOpen) this.open();
  }

  componentWillUnmount() {
    this.closed = true;
  }

  open() {
    this.setState(this.initialState());
    signers.catalogue().subscribe(
      (res) => !this.closed && this.setState({ catalogue: res.data || null }),
      (e) => !this.closed && this.setState({ catalogueError: signService.problemMessage(e, translateService.t("signers.actionError")) })
    );
  }

  // the certification options, anew each time the certification step opens;
  // a certification no longer available is unpicked
  loadOptions = () => {
    this.setState({ options: null, optionsError: null });
    signers.certificationOptions(this.props.workspaceId).subscribe(
      (res) => {
        if (this.closed) return;
        const options = res.data || null;
        this.setState((s) => {
          const still = ((options && options.options) || []).some(
            (o) => s.kind && o.kind === s.kind.kind && o.certification === s.cert.certification && o.available
          );
          return { options, cert: still ? s.cert : { ...s.cert, certification: "" } };
        });
      },
      (e) => !this.closed && this.setState({ optionsError: signService.problemMessage(e, translateService.t("signers.actionError")) })
    );
  };

  // ---- the catalogue -----------------------------------------------------------

  // the kinds the caller may create now, in the catalogue's order (unknown
  // names on either side are left out)
  kinds = () =>
    ((this.state.catalogue && this.state.catalogue.kinds) || []).filter(
      (k) => k.creatable && (this.props.creatableKinds || []).includes(k.kind)
    );

  // the kinds the owner could create in an organization's workspace but not
  // here, with the gateway's reason label
  unavailable = () =>
    ((this.state.catalogue && this.state.catalogue.kinds) || [])
      .map((k) => ({ kind: k, why: (this.props.unavailableKinds || []).find((u) => u.kind === k.kind) }))
      .filter((x) => x.why);

  authEntry = (type) => ((this.state.catalogue && this.state.catalogue.authTypes) || []).find((a) => a.authType === type);

  engineEntry = (engine) => ((this.state.catalogue && this.state.catalogue.engines) || []).find((e) => e.engine === engine);

  // a type can be chosen when it uses no engine, or an engine here verifies it
  choosable = (entry) => !!entry && (entry.engineUse === "NONE" || (entry.engines || []).length > 0);

  // the kind's types this deployment can carry, in the catalogue's order
  allowedTypes = (kind) =>
    ((this.state.catalogue && this.state.catalogue.authTypes) || []).filter(
      (a) => ((kind && kind.allowedAuthTypes) || []).includes(a.authType)
    );

  // the engines every chosen engine-using type is verified by
  engineChoices = (types) => {
    const using = types.map(this.authEntry).filter((a) => a && a.engineUse !== "NONE");
    if (!using.length) return null;
    return using
      .map((a) => a.engines || [])
      .reduce((acc, list) => acc.filter((e) => list.includes(e)))
      .map(this.engineEntry)
      .filter(Boolean);
  };

  isCloud = (kind) => !!kind && (kind.certifications || []).length > 0;

  // ---- the steps ---------------------------------------------------------------

  steps = () => {
    const kind = this.state.kind;
    if (!kind) return ["kind"];
    const steps = ["kind"];
    if (this.isCloud(kind)) steps.push("certification", "subject");
    if (!kind.autoProvisioned) steps.push("details");
    return steps;
  };

  goTo = (step) => {
    this.setState({ step, errors: {}, refusal: null });
    if (step === "certification") this.loadOptions();
  };

  chooseKind = (kind) => {
    const allowed = this.allowedTypes(kind).filter(this.choosable);
    const authTypes = allowed.length === 1 ? [allowed[0].authType] : [];
    this.setState({
      kind,
      cert: emptyCertificate(kind, this.state.name),
      authTypes,
      engine: this.preselectedEngine(authTypes),
      errors: {},
      refusal: null,
    });
  };

  preselectedEngine = (types) => {
    const engines = this.engineChoices(types);
    return engines && engines.length === 1 ? engines[0].engine : "";
  };

  toggleType = (type) => {
    const authTypes = this.state.authTypes.includes(type)
      ? this.state.authTypes.filter((x) => x !== type)
      : [...this.state.authTypes, type];
    const engines = this.engineChoices(authTypes);
    const keep = engines && engines.some((e) => e.engine === this.state.engine);
    this.setState({ authTypes, engine: keep ? this.state.engine : this.preselectedEngine(authTypes), errors: {} });
  };

  // the display name; the name in the certificate follows it until edited
  setName = (name) =>
    this.setState((s) => ({
      name,
      errors: {},
      cert: s.cert.unitEdited ? s.cert : { ...s.cert, organizationalUnit: unitFrom(name) },
    }));

  checkStep = (step) => {
    const { kind, cert, name, authTypes, engine, catalogue, options, evidence } = this.state;
    if (step === "kind") return kind ? {} : { kind: t("errors.kind") };
    if (step === "certification" || step === "subject") return checkCertificate(kind, cert, catalogue, options, step);
    const errors = this.isCloud(kind) ? checkCertificate(kind, cert, catalogue, options, "unit") : {};
    if (!name.trim() || name.trim().length > 255) errors.name = t("errors.name");
    if (!authTypes.length) errors.authTypes = t("errors.authTypes");
    if (keepsEvidence(authTypes) && !evidenceValid(evidence))
      errors.evidence = translateService.t("signers.evidence.atLeastOne");
    const engines = this.engineChoices(authTypes);
    if (engines && !engines.some((e) => e.engine === engine)) errors.engine = t("errors.engine");
    return errors;
  };

  next = () => {
    const steps = this.steps();
    const errors = this.checkStep(this.state.step);
    if (Object.keys(errors).length) return this.setState({ errors });
    const at = steps.indexOf(this.state.step);
    if (at === steps.length - 1) return this.create();
    this.goTo(steps[at + 1]);
  };

  back = () => {
    const steps = this.steps();
    const at = steps.indexOf(this.state.step);
    if (at > 0) this.goTo(steps[at - 1]);
  };

  create = () => {
    const { kind, cert, name, authTypes, engine, catalogue, options, evidence } = this.state;
    // an auto-provisioned kind: the name is the kind's (ignored when the
    // workspace already has its signing tool), the types its own
    const body = kind.autoProvisioned ? { kind: kind.kind, name: kind.label } : { kind: kind.kind, name: name.trim(), authTypes };
    if (!kind.autoProvisioned && this.engineChoices(authTypes)) body.verificationEngine = engine;
    // every member sent: an absent one would be on, the PDF included
    if (!kind.autoProvisioned && keepsEvidence(authTypes)) body.verificationEvidence = evidenceBody(evidence);
    if (this.isCloud(kind)) Object.assign(body, certificateBody(cert, catalogue, options));
    this.setState({ sending: true, refusal: null });
    signers.create(this.props.workspaceId, body).subscribe(
      (res) => {
        if (this.closed) return;
        this.setState({ sending: false });
        this.props.onCreated(res.data);
      },
      (e) => !this.closed && this.refused(e)
    );
  };

  // A refusal, shown where it can be put right: a certification no longer
  // open (the options again, the option disabled), a vetted organization's
  // records changed meanwhile (close and start again), the organization's
  // name too long (the typed subject), a subject member it names (its field);
  // the rest as the gateway words it.
  refused = (e) => {
    const code = signService.problemCode(e);
    const body = (e && e.response && e.response.data) || {};
    const detail = signService.problemMessage(e, t("createError"));
    this.setState({ sending: false });
    if (vettedSubjectRefused(code, body, this.state.options))
      return this.setState({ step: "subject", errors: {}, refusal: translateService.t("signers.subject.changed") });
    if (code === "CERTIFICATION_NOT_AVAILABLE") {
      this.goTo("certification");
      return this.setState({ refusal: detail });
    }
    if (code === "ORGANIZATION_NAME_TOO_LONG") {
      this.setState({ step: "subject" });
      return this.setState({ errors: { organizationName: detail } });
    }
    const member = (code === "SUBJECT_REQUIRED" || code === "PARAMETER_IS_INVALID") && subjectFieldOf(body);
    if (member === "organizationalUnit") return this.setState({ step: "details", errors: { organizationalUnit: detail } });
    if (member) return this.setState({ step: "subject", errors: { [member]: detail } });
    const refusal =
      code === "VERIFICATION_ENGINE_UNAVAILABLE" || code === "SIGNER_QUEUE_UNAVAILABLE"
        ? translateService.t(code === "SIGNER_QUEUE_UNAVAILABLE" ? "signers.queueUnavailable" : "signers.engineUnreachable")
        : detail;
    this.setState({ refusal });
  };

  // ---- rendering ---------------------------------------------------------------

  renderKind() {
    const kinds = this.kinds();
    const unavailable = this.unavailable();
    const chosen = this.state.kind;
    return (
      <fieldset className="signer-dialog__group">
        <legend>{t("kind")}</legend>
        {/* nothing to add here but what an organization's account offers */}
        {!kinds.length && unavailable.length > 0 && <p className="signer-dialog__hint">{translateService.t("signers.noOption")}</p>}
        {kinds.map((k) => (
          <label key={k.kind} className="signer-dialog__choice">
            <input type="radio" name="kind" checked={!!chosen && chosen.kind === k.kind} onChange={() => this.chooseKind(k)} />
            <span>
              <strong>{k.onePerWorkspace ? t("useWorkspaces", k.label) : k.label}</strong>
              {k.onePerWorkspace && <small>{t("onePerWorkspace")}</small>}
            </span>
          </label>
        ))}
        {unavailable.map(({ kind, why }) => (
          <label key={kind.kind} className="signer-dialog__choice is-off">
            <input type="radio" name="kind" disabled checked={false} onChange={() => {}} />
            <span>
              <strong>{kind.label}</strong>
              {why.reasonLabel && <small>{why.reasonLabel}</small>}
            </span>
          </label>
        ))}
        {this.state.errors.kind && <em className="signer-dialog__error">{this.state.errors.kind}</em>}
        {/* once in the dialog, when a cloud kind is chosen */}
        {this.isCloud(chosen) && <CloudSignerNotice />}
      </fieldset>
    );
  }

  renderDetails() {
    const { kind, name, authTypes, engine, errors, cert } = this.state;
    const types = this.allowedTypes(kind);
    const engines = this.engineChoices(authTypes);
    return (
      <React.Fragment>
        <label className="signer-dialog__field" htmlFor="signer-name">
          <span>{t("name")}</span>
          <input id="signer-name" type="text" maxLength={255} value={name} onChange={(e) => this.setName(e.target.value)} />
          {errors.name && <em className="signer-dialog__error">{errors.name}</em>}
        </label>
        {/* a cloud kind: the clean name its certificate carries, below the display name */}
        {this.isCloud(kind) && (
          <UnitField
            value={cert}
            error={errors.organizationalUnit}
            onChange={(patch) => this.setState((s) => ({ cert: { ...s.cert, ...patch }, errors: {} }))}
          />
        )}
        <fieldset className="signer-dialog__group">
          <legend>{t("authTypes")}</legend>
          {types.map((a) => {
            const off = !this.choosable(a);
            return (
              <label key={a.authType} className={`signer-dialog__choice ${off ? "is-off" : ""}`}>
                <input
                  type="checkbox"
                  disabled={off}
                  checked={authTypes.includes(a.authType)}
                  onChange={() => this.toggleType(a.authType)}
                />
                <span>
                  <strong>{a.label}</strong>
                  {off && <small>{t("noEngine")}</small>}
                </span>
              </label>
            );
          })}
          {errors.authTypes && <em className="signer-dialog__error">{errors.authTypes}</em>}
        </fieldset>
        {/* one engine: preselected and not asked */}
        {engines && engines.length !== 1 && (
          <label className="signer-dialog__field" htmlFor="signer-engine">
            <span>{t("engine")}</span>
            {engines.length ? (
              <select id="signer-engine" value={engine} onChange={(e) => this.setState({ engine: e.target.value, errors: {} })}>
                <option value="" />
                {engines.map((e) => (
                  <option key={e.engine} value={e.engine}>
                    {e.label}
                  </option>
                ))}
              </select>
            ) : (
              <small>{t("noEngine")}</small>
            )}
            {errors.engine && <em className="signer-dialog__error">{errors.engine}</em>}
          </label>
        )}
        {/* face verification: which of the provider's files each signing keeps */}
        {keepsEvidence(authTypes) && (
          <EvidenceFields
            value={this.state.evidence}
            error={errors.evidence}
            onChange={(evidence) => this.setState({ evidence, errors: {} })}
          />
        )}
      </React.Fragment>
    );
  }

  renderStep() {
    const { step, kind, cert, catalogue, options, optionsError, errors } = this.state;
    if (step === "kind") return this.renderKind();
    if (step === "certification" || step === "subject") {
      if (optionsError)
        return (
          <p className="signer-dialog__error" role="alert">
            {optionsError}{" "}
            <button type="button" className="signer-dialog__link" onClick={this.loadOptions}>
              {translateService.t("signature.retry")}
            </button>
          </p>
        );
      if (!options) return <img src="./assets/images/black-loading.svg" className="content-loader" alt="" />;
      return (
        <CertificateFields
          kind={kind}
          catalogue={catalogue}
          options={options}
          value={cert}
          errors={errors}
          part={step}
          onChange={(patch) => this.setState((s) => ({ cert: { ...s.cert, ...patch }, errors: {} }))}
        />
      );
    }
    return this.renderDetails();
  }

  render() {
    const { catalogue, catalogueError, step, kind, sending, refusal } = this.state;
    const steps = this.steps();
    const last = steps.indexOf(step) === steps.length - 1;
    const anything = this.kinds().length > 0 || this.unavailable().length > 0;
    let body;
    if (catalogueError) body = <p className="signer-dialog__error">{catalogueError}</p>;
    else if (!catalogue) body = <img src="./assets/images/black-loading.svg" className="content-loader" alt="" />;
    else if (!anything) body = <p>{t("nothing")}</p>;
    else body = this.renderStep();
    return (
      <Modal isOpen={!!this.props.isOpen} fade={false} className={`signer-dialog ${translateService.key}`} toggle={this.props.onClose}>
        <ModalHeader>
          <span>{t("title")}</span>
        </ModalHeader>
        <ModalBody>
          <div className="signer-dialog__scroll">
            {body}
            {refusal && (
              <p className="signer-dialog__error" role="alert">
                {refusal}
              </p>
            )}
          </div>
          <div className="buttons signer-dialog__footer">
            {steps.indexOf(step) > 0 && (
              <LoadingButton type="button" className="button background-color-one color-white" disabled={sending} onClick={this.back}>
                {t("back")}
              </LoadingButton>
            )}
            {catalogue && this.kinds().length > 0 && (
              <LoadingButton type="button" className="button background-color-two color-white" loading={sending} onClick={this.next}>
                {last ? (kind && kind.onePerWorkspace ? t("useWorkspaces", kind.label) : t("create")) : t("next")}
              </LoadingButton>
            )}
            <LoadingButton type="button" className="button background-color-one color-white" disabled={sending} onClick={this.props.onClose}>
              {t("cancel")}
            </LoadingButton>
          </div>
        </ModalBody>
      </Modal>
    );
  }
}
